/**
 * KpiDashboard.tsx — Admin-only Revenue KPI dashboard (Phase 05-08 → Phase 07-05).
 *
 * Layout (07-05: Surface A — admin earnings across all creators):
 *   Demo badge (visible when VITE_DEMO_DATA==='true'; D-07-07)
 *   Period selector: Today / 7d / 30d / Month-to-date / Custom From-To (D-07-08)
 *   Multi-creator filter (D-07-09; scopes all panels together)
 *   Row 1: 4 summary cards — Gross revenue (headline) + net/payout (secondary) / ARPU / PPV-unlock rate / Avg response time (D-07-01)
 *   Row 1.5: Source-breakdown bar (D-07-02, D-07-06 — hand-rolled, no chart lib)
 *   Row 2: per-creator Table
 *   Row 3: filterable Transactions list (date/creator/fan/kind/status/gross/net) with kind/status filters + load-more cursor
 *   Row 4: collapsible Top spenders Table
 *
 * Security (T-05-28):
 *   - Admin-only page — RolesGuard on the server enforces Role.Admin at /kpi/*.
 *   - Financial data is admin-only. Never shown to chatters (no chatter route leads here).
 *   - is_whale field from top-spenders is admin-only; chatter role gets it stripped server-side.
 *
 * D-05-15: Revenue at account/creator level only (per-chatter DEFERRED).
 * T-05-31: Unattributed revenue column always present (never silently dropped).
 * D-07-01: Gross as headline; net/payout as secondary on the revenue summary card.
 * D-07-06: Source breakdown bar = hand-rolled CSS only; no chart lib installed.
 * D-07-07: Demo badge shown while reading seeded data (VITE_DEMO_DATA env flag).
 * D-07-08: Period presets + custom From/To (AuditLog.tsx pattern).
 * D-07-09: kpiApi.creators/topSpenders/breakdown/transactions all accept creatorIds.
 * T-07-05-02: Single formatEur(minor)=€(minor/100) formatter; never re-multiply.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  kpiApi,
  type CreatorKpiDto,
  type TopSpenderDto,
  type TransactionRowDto,
  type KindBreakdownEntry,
} from '../../lib/api-client.js';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Badge,
  Select,
  Button,
  Input,
} from '@crm/ui';
import { BreakdownBar } from './BreakdownBar.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format minor units (cents) to EUR string with 2 decimal places. T-07-05-02: never re-multiply. */
function formatEur(minor: number): string {
  return `€${(minor / 100).toFixed(2)}`;
}

/** Format an ISO date string in Berlin timezone (DE locale). */
function formatBerlin(isoOrDate: string | Date | null): string {
  if (!isoOrDate) return '—';
  return new Date(isoOrDate).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' });
}

/** Period options for the summary card selector. */
type Period = 'today' | '7d' | '30d' | 'mtd' | 'custom';

const PERIODS: Array<{ value: Period; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: '7d',   label: '7 days' },
  { value: '30d',  label: '30 days' },
  { value: 'mtd',  label: 'Month to date' },
  { value: 'custom', label: 'Custom range' },
];

/** Compute the since/until ISO string for a period. Custom period uses supplied from/to. */
function periodToRange(
  period: Period,
  customFrom?: string,
  customTo?: string,
): { since: string; until: string; customRangeError?: string } {
  const until = new Date();
  const since = new Date();

  if (period === 'today') {
    since.setHours(0, 0, 0, 0);
  } else if (period === '7d') {
    since.setDate(since.getDate() - 7);
  } else if (period === '30d') {
    since.setDate(since.getDate() - 30);
  } else if (period === 'mtd') {
    // First of current month, midnight
    since.setDate(1);
    since.setHours(0, 0, 0, 0);
  } else if (period === 'custom') {
    // WR-06: validate custom date inputs before calling .toISOString().
    // An empty or malformed datetime-local value produces an Invalid Date (isNaN);
    // from > to is a backwards range that returns zero rows with no user feedback.
    const fromDate = customFrom ? new Date(customFrom) : null;
    const toDate = customTo ? new Date(customTo) : null;

    if (!fromDate || isNaN(fromDate.getTime())) {
      // Fall back to 30-day default and surface an error to the UI
      since.setDate(since.getDate() - 30);
      return { since: since.toISOString(), until: until.toISOString(), customRangeError: 'Invalid "From" date.' };
    }
    if (!toDate || isNaN(toDate.getTime())) {
      return { since: fromDate.toISOString(), until: until.toISOString(), customRangeError: 'Invalid "To" date.' };
    }
    if (fromDate >= toDate) {
      return { since: fromDate.toISOString(), until: toDate.toISOString(), customRangeError: '"From" must be before "To".' };
    }
    return { since: fromDate.toISOString(), until: toDate.toISOString() };
  }

  return { since: since.toISOString(), until: until.toISOString() };
}

/**
 * The 4 known transaction kinds (D-07-02) as multi-select chip options
 * (owner ask 2026-07-02: filter several earning sources at once, e.g. PPV + Tip).
 * Empty selection = all kinds.
 */
const KIND_CHIP_OPTIONS = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'ppv_unlock', label: 'PPV' },
  { value: 'tip', label: 'Tip' },
  { value: 'other', label: 'Other' },
] as const;
const TX_STATUS_VALUES = ['', 'APPROVED', 'REVERSED'] as const;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * SummaryCard — one of the 4 KPI metric cards in Row 1.
 *
 * Shows the metric value (text-lg font-semibold font-mono) and an optional
 * secondary line for net/payout (D-07-01), and an optional delta badge.
 */
function SummaryCard({
  title,
  value,
  secondary,
  delta,
  deltaSign,
}: {
  title: string;
  value: string;
  secondary?: string;
  delta?: string;
  deltaSign?: 'positive' | 'negative' | 'neutral';
}) {
  const deltaColor =
    deltaSign === 'positive'
      ? 'text-success'
      : deltaSign === 'negative'
      ? 'text-danger'
      : 'text-text-muted';

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ fontSize: '14px', fontWeight: 600 }}>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          style={{
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: '"IBM Plex Mono", "JetBrains Mono", monospace',
            color: 'var(--color-text)',
          }}
        >
          {value}
        </div>
        {secondary && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              marginTop: '2px',
              fontFamily: '"IBM Plex Mono", "JetBrains Mono", monospace',
            }}
          >
            {secondary}
          </div>
        )}
        {delta && (
          <div
            className={`text-xs font-mono ${deltaColor}`}
            style={{ fontSize: '12px', fontFamily: '"IBM Plex Mono", "JetBrains Mono", monospace' }}
          >
            {delta}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * CreatorTable — per-creator revenue breakdown with unattributed column.
 *
 * T-05-31: unattributed_gross_minor is always shown (even when zero), in warning color.
 */
function CreatorTable({ rows }: { rows: CreatorKpiDto[] }) {
  const [sortKey, setSortKey] = React.useState<keyof CreatorKpiDto>('revenue_gross_minor');
  const [sortAsc, setSortAsc] = React.useState(false);

  const sorted = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[sortKey] as number;
      const bv = b[sortKey] as number;
      return sortAsc ? av - bv : bv - av;
    });
  }, [rows, sortKey, sortAsc]);

  function handleSort(key: keyof CreatorKpiDto) {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Per-creator breakdown</CardTitle>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Creator</TableHead>
              <TableHead
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('revenue_gross_minor')}
              >
                Revenue {sortKey === 'revenue_gross_minor' ? (sortAsc ? '↑' : '↓') : ''}
              </TableHead>
              <TableHead
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('fan_count')}
              >
                Fans {sortKey === 'fan_count' ? (sortAsc ? '↑' : '↓') : ''}
              </TableHead>
              <TableHead
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('arpu_minor')}
              >
                ARPU {sortKey === 'arpu_minor' ? (sortAsc ? '↑' : '↓') : ''}
              </TableHead>
              <TableHead
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('ppv_unlocks')}
              >
                PPV unlocks {sortKey === 'ppv_unlocks' ? (sortAsc ? '↑' : '↓') : ''}
              </TableHead>
              {/* Unattributed revenue — always present (T-05-31) */}
              <TableHead
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('unattributed_gross_minor')}
                title="Revenue with no active chatter assignment at transaction time."
              >
                Unattributed{' '}
                {sortKey === 'unattributed_gross_minor' ? (sortAsc ? '↑' : '↓') : ''}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  No revenue data for this period.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => (
                <TableRow key={row.creatorId}>
                  <TableCell style={{ fontWeight: 600 }}>{row.creatorName}</TableCell>
                  <TableCell
                    style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}
                  >
                    {formatEur(row.revenue_gross_minor)}
                  </TableCell>
                  <TableCell>{row.fan_count}</TableCell>
                  <TableCell
                    style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}
                  >
                    {formatEur(row.arpu_minor)}
                  </TableCell>
                  <TableCell>{row.ppv_unlocks}</TableCell>
                  <TableCell
                    title="Revenue with no active chatter assignment at transaction time."
                    style={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontSize: '12px',
                      color: row.unattributed_gross_minor > 0 ? 'var(--color-warning)' : 'var(--color-text-muted)',
                    }}
                  >
                    {formatEur(row.unattributed_gross_minor)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * TopSpendersTable — collapsible table of top-spending fans.
 *
 * ★ prefix in warning color for whale fans (is_whale=true — admin only).
 */
function TopSpendersTable({
  rows,
  since,
  until,
  creatorIds,
}: {
  rows: TopSpenderDto[];
  since: string;
  until: string;
  creatorIds: string[];
}) {
  const [open, setOpen] = React.useState(true);

  return (
    <Card>
      <CardHeader
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen((v) => !v)}
      >
        <CardTitle>
          Top spenders {open ? '▾' : '▸'}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent style={{ padding: 0 }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fan</TableHead>
                <TableHead>Creator</TableHead>
                <TableHead>Total spend</TableHead>
                <TableHead>PPV purchases</TableHead>
                <TableHead>Last activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No revenue data for this period.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={`${row.fanId}-${row.creatorId}`}>
                    <TableCell>
                      {row.is_whale && (
                        <span
                          style={{ color: 'var(--color-warning)', marginRight: '4px' }}
                          title="Top spender (whale)"
                          aria-label="Whale fan"
                        >
                          ★
                        </span>
                      )}
                      <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}>
                        {row.username ?? 'Fan'}
                      </span>
                    </TableCell>
                    <TableCell>{row.creatorName}</TableCell>
                    <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}>
                      {formatEur(row.total_spend_gross_minor)}
                    </TableCell>
                    <TableCell>{row.ppv_count}</TableCell>
                    <TableCell style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      {formatBerlin(row.last_activity)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      )}
    </Card>
  );
}

/**
 * TransactionsTable — filterable paginated transactions list (D-07-09).
 *
 * Shows date/creator/fan/kind/status/gross/net. kind/status filter controls + "Load more" cursor.
 * T-07-05-02: formatEur(minor) only, never re-multiply.
 */
function TransactionsTable({
  rows,
  isLoading,
  hasMore,
  onLoadMore,
  kindFilters,
  statusFilter,
  onKindFiltersChange,
  onStatusFilterChange,
}: {
  rows: TransactionRowDto[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /** Selected kinds (multi-select). Empty array = all kinds. */
  kindFilters: string[];
  statusFilter: string;
  onKindFiltersChange: (kinds: string[]) => void;
  onStatusFilterChange: (status: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <CardTitle>Transactions</CardTitle>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              {kindFilters.length === 0 ? 'All kinds' : `${kindFilters.length} kind${kindFilters.length > 1 ? 's' : ''}`}:
            </span>
            <div
              role="group"
              aria-label="Filter by transaction kind (multiple allowed)"
              style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}
            >
              {KIND_CHIP_OPTIONS.map((opt) => {
                const isSelected = kindFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      onKindFiltersChange(
                        isSelected
                          ? kindFilters.filter((k) => k !== opt.value)
                          : [...kindFilters, opt.value],
                      )
                    }
                    style={{
                      padding: '4px 10px',
                      borderRadius: '16px',
                      border: '1px solid var(--color-border)',
                      background: isSelected ? 'var(--color-accent)' : 'var(--color-bg-hover)',
                      color: isSelected ? 'white' : 'var(--color-text)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    aria-pressed={isSelected}
                    aria-label={`${isSelected ? 'Remove' : 'Add'} kind filter ${opt.label}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {kindFilters.length > 0 && (
                <button
                  type="button"
                  onClick={() => onKindFiltersChange([])}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '16px',
                    border: '1px solid var(--color-border)',
                    background: 'transparent',
                    color: 'var(--color-text-muted)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  All
                </button>
              )}
            </div>
            <label
              htmlFor="tx-status-filter"
              style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}
            >
              Status:
            </label>
            <Select
              id="tx-status-filter"
              value={statusFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onStatusFilterChange(e.target.value)
              }
            >
              <option value="">All statuses</option>
              <option value="APPROVED">Approved</option>
              <option value="REVERSED">Reversed</option>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date (Berlin)</TableHead>
              <TableHead>Creator</TableHead>
              <TableHead>Fan</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Gross</TableHead>
              <TableHead>Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  Loading transactions…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  No transactions for this period / filter combination.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={`${row.id}`}>
                  <TableCell style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {formatBerlin(row.occurred_at)}
                  </TableCell>
                  <TableCell style={{ fontSize: '12px' }}>
                    {row.creatorName ?? '—'}
                  </TableCell>
                  <TableCell style={{ fontSize: '12px' }}>
                    {row.fanNickname ?? row.fanUsername ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge>{row.tx_status === 'REVERSED' ? `${row.category ?? 'other'} ↩` : (row.category ?? 'other')}</Badge>
                  </TableCell>
                  <TableCell>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: row.tx_status === 'REVERSED'
                          ? 'var(--color-danger)'
                          : 'var(--color-success)',
                      }}
                    >
                      {row.tx_status ?? '—'}
                    </span>
                  </TableCell>
                  {/* Gross = list price = Maloum's "net" (the tip / booked amount). */}
                  <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}>
                    {row.amount_net_minor != null ? formatEur(row.amount_net_minor) : '—'}
                  </TableCell>
                  {/* Net = creator take-home after Maloum fees = Maloum's headline (payout). */}
                  <TableCell
                    style={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {row.amount_payout_minor != null ? formatEur(row.amount_payout_minor) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {/* Owner naming (2026-07-03): Gross = list price (Maloum's "net"); Net = creator
            take-home (Maloum's headline "payout"). The VAT-inclusive fan charge is not shown. */}
        <div style={{ padding: '8px 12px 0', fontSize: '11px', color: 'var(--color-text-muted)' }}>
          Gross = list price · Net = creator take-home after Maloum fees
        </div>
        {hasMore && (
          <div style={{ padding: '12px', textAlign: 'center' }}>
            <Button onClick={onLoadMore} disabled={isLoading}>
              Load more
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// KpiDashboard — main component
// ---------------------------------------------------------------------------

/**
 * KpiDashboard — Admin-only Revenue dashboard (Surface A, Phase 07-05).
 *
 * Admin-only: this page is never mounted for chatters (no route; admin app only).
 * Revenue data is account/creator-level (D-05-15; per-chatter DEFERRED).
 */
export function KpiDashboard() {
  // ---- Period state -------------------------------------------------------
  // Default = 30d: last month's earnings viewed early in a new month fall outside 7d,
  // which made the default view land empty for the owner.
  const [period, setPeriod] = React.useState<Period>('30d');
  const [customFrom, setCustomFrom] = React.useState('');
  const [customTo, setCustomTo] = React.useState('');
  // Applied custom range (only updated on form submit)
  const [appliedCustomFrom, setAppliedCustomFrom] = React.useState('');
  const [appliedCustomTo, setAppliedCustomTo] = React.useState('');

  const { since, until, customRangeError } = React.useMemo(
    () => periodToRange(period, appliedCustomFrom, appliedCustomTo),
    [period, appliedCustomFrom, appliedCustomTo],
  );

  function handleApplyCustom(e: React.FormEvent) {
    e.preventDefault();
    setAppliedCustomFrom(customFrom);
    setAppliedCustomTo(customTo);
  }

  // ---- Multi-creator filter state -----------------------------------------
  const [selectedCreatorIds, setSelectedCreatorIds] = React.useState<string[]>([]);

  // ---- Transaction filter state -------------------------------------------
  const [txKindFilters, setTxKindFilters] = React.useState<string[]>([]);
  const [txStatusFilter, setTxStatusFilter] = React.useState('');
  const [txPages, setTxPages] = React.useState<TransactionRowDto[][]>([]);
  const [txNextCursor, setTxNextCursor] = React.useState<string | null>(null);
  const [txLoadingMore, setTxLoadingMore] = React.useState(false);

  // ---- Queries ------------------------------------------------------------

  const creatorsQuery = useQuery({
    queryKey: ['kpi', 'creators', since, until, selectedCreatorIds],
    queryFn: () => kpiApi.creators(since, until, selectedCreatorIds.length ? selectedCreatorIds : undefined),
    staleTime: 60_000,
  });

  const topSpendersQuery = useQuery({
    queryKey: ['kpi', 'topSpenders', since, until, selectedCreatorIds],
    queryFn: () => kpiApi.topSpenders(20, since, until, selectedCreatorIds.length ? selectedCreatorIds : undefined),
    staleTime: 60_000,
  });

  const breakdownQuery = useQuery({
    queryKey: ['kpi', 'breakdown', since, until, selectedCreatorIds],
    queryFn: () => kpiApi.breakdown(since, until, selectedCreatorIds.length ? selectedCreatorIds : undefined),
    staleTime: 60_000,
  });

  // Transactions: refetch from page 1 whenever period/filter/creatorIds change
  const txQueryKey = ['kpi', 'transactions', since, until, selectedCreatorIds, txKindFilters, txStatusFilter];
  const transactionsQuery = useQuery({
    queryKey: txQueryKey,
    queryFn: async () => {
      const result = await kpiApi.transactions({
        since,
        until,
        kinds: txKindFilters.length ? txKindFilters : undefined,
        status: txStatusFilter || undefined,
        creatorIds: selectedCreatorIds.length ? selectedCreatorIds : undefined,
        limit: 20,
      });
      return result;
    },
    staleTime: 60_000,
  });

  // Reset accumulated pages when primary query refires (queryKey changes)
  React.useEffect(() => {
    if (transactionsQuery.data) {
      setTxPages([transactionsQuery.data.data]);
      setTxNextCursor(transactionsQuery.data.next);
    }
  }, [transactionsQuery.data]);

  async function handleLoadMore() {
    if (!txNextCursor || txLoadingMore) return;
    setTxLoadingMore(true);
    try {
      const result = await kpiApi.transactions({
        since,
        until,
        kinds: txKindFilters.length ? txKindFilters : undefined,
        status: txStatusFilter || undefined,
        creatorIds: selectedCreatorIds.length ? selectedCreatorIds : undefined,
        limit: 20,
        next: txNextCursor,
      });
      setTxPages((prev) => [...prev, result.data]);
      setTxNextCursor(result.next);
    } finally {
      setTxLoadingMore(false);
    }
  }

  // Aggregate breakdown entries across creators — sum by kind (D-07-02)
  const aggregatedBreakdown = React.useMemo((): KindBreakdownEntry[] => {
    const entries = breakdownQuery.data ?? [];
    const byKind = new Map<string, KindBreakdownEntry>();
    for (const entry of entries) {
      const existing = byKind.get(entry.kind);
      if (existing) {
        existing.gross_minor += entry.gross_minor;
        existing.net_minor += entry.net_minor;
        existing.payout_minor += entry.payout_minor;
        existing.count += entry.count;
      } else {
        byKind.set(entry.kind, { ...entry });
      }
    }
    // Return in a stable order
    return ['subscription', 'ppv_unlock', 'tip', 'other']
      .map((k) => byKind.get(k))
      // Owner naming (2026-07-03): filter on list-price basis (net_minor), the value we display.
      .filter((e): e is KindBreakdownEntry => e !== undefined && e.net_minor > 0);
  }, [breakdownQuery.data]);

  // Owner ask (2026-07-03): total revenue with an explicit per-source breakdown
  // (PPV / Tips / Subscription). Displayed as Gross (= list price, r.net) and Net
  // (= creator take-home, r.payout); the VAT-inclusive charge (r.gross) is computed but
  // not shown. The three named sources always appear (even at €0 so they read
  // "separately"); "Other" shows only when it carries money, so rows sum to the Total.
  const sourceBreakdown = React.useMemo(() => {
    const entries = breakdownQuery.data ?? [];
    const byKind = new Map<string, { gross: number; net: number; payout: number }>();
    for (const k of ['subscription', 'ppv_unlock', 'tip', 'other']) {
      byKind.set(k, { gross: 0, net: 0, payout: 0 });
    }
    for (const e of entries) {
      const agg = byKind.get(e.kind) ?? { gross: 0, net: 0, payout: 0 };
      agg.gross += e.gross_minor;
      agg.net += e.net_minor;
      agg.payout += e.payout_minor;
      byKind.set(e.kind, agg);
    }
    const ORDER: Array<{ kind: string; label: string }> = [
      { kind: 'subscription', label: 'Subscription' },
      { kind: 'ppv_unlock', label: 'PPV' },
      { kind: 'tip', label: 'Tip' },
      { kind: 'other', label: 'Other' },
    ];
    const rows = ORDER
      .map(({ kind, label }) => ({ kind, label, ...byKind.get(kind)! }))
      // Always show the three named sources; show "Other" only when it carries money.
      .filter((r) => r.kind !== 'other' || r.gross !== 0 || r.net !== 0 || r.payout !== 0);
    const totals = rows.reduce(
      (acc, r) => ({ gross: acc.gross + r.gross, net: acc.net + r.net, payout: acc.payout + r.payout }),
      { gross: 0, net: 0, payout: 0 },
    );
    return { rows, totals };
  }, [breakdownQuery.data]);

  // Compute summary metrics from creator rows (D-07-01: gross headline + net secondary)
  const summary = React.useMemo(() => {
    const rows = creatorsQuery.data ?? [];
    const totalRevenue = rows.reduce((s, r) => s + r.revenue_gross_minor, 0);
    const totalFans = rows.reduce((s, r) => s + r.fan_count, 0);
    const totalPpv = rows.reduce((s, r) => s + r.ppv_unlocks, 0);
    const arpu = totalFans > 0 ? Math.round(totalRevenue / totalFans) : 0;
    const ppvRate = totalFans > 0 ? ((totalPpv / totalFans) * 100).toFixed(1) : '0.0';

    // WR-03 fix: compute net/payout sum over the UNFILTERED breakdown data, not over
    // aggregatedBreakdown (which excludes kinds with gross_minor === 0). Using the
    // gross-filtered set for the net headline is a category error: a kind could have
    // payout but zero gross (e.g. a refund/credit artifact) and would be silently dropped.
    const allBreakdownEntries = breakdownQuery.data ?? [];
    const netTotal = allBreakdownEntries.reduce((s, e) => s + e.payout_minor, 0);

    return { totalRevenue, netTotal, arpu, totalPpv, ppvRate };
  }, [creatorsQuery.data, breakdownQuery.data]);

  const isLoading = creatorsQuery.isLoading;
  const isError = creatorsQuery.isError;
  const error = creatorsQuery.error as Error | null;

  // All accumulated transaction rows
  const allTxRows = React.useMemo(() => txPages.flat(), [txPages]);

  const currentPeriodLabel = PERIODS.find((p) => p.value === period)?.label ?? period;

  return (
    <div
      style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '24px',
      }}
    >
      {/* D-07-07: Demo data badge — visible while VITE_DEMO_DATA==='true' */}
      {import.meta.env.VITE_DEMO_DATA === 'true' && (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: 'var(--color-warning)',
            color: 'var(--color-bg)',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          Demo data — not live earnings
        </div>
      )}

      {/* Page header + period selector */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <h1 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text)' }}>
          Revenue
        </h1>
        {/* Period selector (D-07-08) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label
            htmlFor="kpi-period"
            style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}
          >
            Period:
          </label>
          <Select
            id="kpi-period"
            value={period}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setPeriod(e.target.value as Period)
            }
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Custom date-range form (D-07-08 — shown only when period==='custom') */}
      {period === 'custom' && (
        <Card>
          <CardContent className="pt-3">
            <form
              onSubmit={handleApplyCustom}
              className="grid grid-cols-3 gap-3 items-end"
            >
              <Input
                label="From"
                type="datetime-local"
                value={customFrom}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomFrom(e.target.value)}
              />
              <Input
                label="To"
                type="datetime-local"
                value={customTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTo(e.target.value)}
              />
              <Button type="submit">Apply</Button>
            </form>
            {/* WR-06: show inline validation error for invalid/backwards dates */}
            {customRangeError && (
              <p
                role="alert"
                style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-danger)' }}
              >
                {customRangeError}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Multi-creator filter (D-07-09) */}
      {creatorsQuery.data && creatorsQuery.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: '14px' }}>Creator filter</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                {selectedCreatorIds.length === 0
                  ? 'All creators'
                  : `${selectedCreatorIds.length} selected`}:
              </span>
              {creatorsQuery.data.map((c) => {
                const isSelected = selectedCreatorIds.includes(c.creatorId);
                return (
                  <button
                    key={c.creatorId}
                    type="button"
                    onClick={() => {
                      setSelectedCreatorIds((prev) =>
                        isSelected
                          ? prev.filter((id) => id !== c.creatorId)
                          : [...prev, c.creatorId],
                      );
                    }}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '16px',
                      border: '1px solid var(--color-border)',
                      background: isSelected ? 'var(--color-accent)' : 'var(--color-bg-hover)',
                      color: isSelected ? 'white' : 'var(--color-text)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    aria-pressed={isSelected}
                    aria-label={`${isSelected ? 'Deselect' : 'Select'} creator ${c.creatorName}`}
                  >
                    {c.creatorName}
                  </button>
                );
              })}
              {selectedCreatorIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedCreatorIds([])}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '16px',
                    border: '1px solid var(--color-border)',
                    background: 'transparent',
                    color: 'var(--color-text-muted)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {isError && (
        <div style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
          Could not load revenue data.{' '}
          {error?.message ?? 'Check that the seed data has been loaded.'}
        </div>
      )}

      {/* Row 1: Summary cards (grid-cols-4) — D-07-01: gross headline + net secondary */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
        }}
      >
        <SummaryCard
          title={`Total revenue (${currentPeriodLabel})`}
          value={isLoading ? '…' : formatEur(summary.totalRevenue)}
          secondary={
            !isLoading && aggregatedBreakdown.length > 0
              ? `Net: ${formatEur(summary.netTotal)}`
              : undefined
          }
        />
        <SummaryCard
          title="ARPU"
          value={isLoading ? '…' : formatEur(summary.arpu)}
        />
        <SummaryCard
          title="PPV unlock rate"
          value={isLoading ? '…' : `${summary.ppvRate}%`}
        />
        {/* Response time requires sent_at + received_at join — deferred */}
        <SummaryCard
          title="Avg. response time"
          value="—"
        />
      </div>

      {/* Row 1.5: Source-breakdown bar (D-07-02, D-07-06) */}
      <Card>
        <CardHeader>
          <CardTitle>Earnings by source</CardTitle>
        </CardHeader>
        <CardContent>
          {breakdownQuery.isLoading ? (
            <div style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>
              Loading breakdown…
            </div>
          ) : (
            <>
              <BreakdownBar entries={aggregatedBreakdown} />

              {/* Owner ask (2026-07-03): explicit per-source table with a reconciling Total.
                  Gross = list price (Maloum's "net"); Net = creator take-home (Maloum's "payout"). */}
              <div style={{ marginTop: '16px', overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Gross</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sourceBreakdown.rows.map((r) => (
                      <TableRow key={r.kind}>
                        <TableCell style={{ fontWeight: 600 }}>{r.label}</TableCell>
                        {/* Gross = list price (r.net); Net = creator take-home (r.payout). */}
                        <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', textAlign: 'right' }}>
                          {formatEur(r.net)}
                        </TableCell>
                        <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', textAlign: 'right' }}>
                          {formatEur(r.payout)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell style={{ fontWeight: 700 }}>Total</TableCell>
                      {/* Gross = list price (totals.net); Net = creator take-home (totals.payout). */}
                      <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>
                        {formatEur(sourceBreakdown.totals.net)}
                      </TableCell>
                      <TableCell style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', fontWeight: 700, textAlign: 'right' }}>
                        {formatEur(sourceBreakdown.totals.payout)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '6px', marginBottom: 0 }}>
                  Gross = list price · Net = creator take-home after Maloum fees
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Row 2: Per-creator table */}
      {isLoading ? (
        <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
          Loading revenue data…
        </div>
      ) : (
        <CreatorTable rows={creatorsQuery.data ?? []} />
      )}

      {/* Row 3: Transactions list (D-07-09) */}
      <TransactionsTable
        rows={allTxRows}
        isLoading={transactionsQuery.isLoading || txLoadingMore}
        hasMore={txNextCursor !== null}
        onLoadMore={handleLoadMore}
        kindFilters={txKindFilters}
        statusFilter={txStatusFilter}
        onKindFiltersChange={(kinds) => {
          setTxKindFilters(kinds);
          setTxPages([]);
          setTxNextCursor(null);
        }}
        onStatusFilterChange={(status) => {
          setTxStatusFilter(status);
          setTxPages([]);
          setTxNextCursor(null);
        }}
      />

      {/* Row 4: Top spenders (collapsible) */}
      {topSpendersQuery.isLoading ? (
        <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
          Loading top spenders…
        </div>
      ) : (
        <TopSpendersTable
          rows={topSpendersQuery.data ?? []}
          since={since}
          until={until}
          creatorIds={selectedCreatorIds}
        />
      )}
    </div>
  );
}
